import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle, 
  Clock, 
  Users, 
  Award, 
  Phone, 
  Mail, 
  MapPin, 
  ChevronRight,
  Microscope,
  FileCheck,
  Truck,
  QrCode,
  MessageCircle,
  ArrowRight,
  Menu,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navbar */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 shrink-0 mr-6 lg:mr-10">
              <img 
                src="/sterisul-logo.png" 
                alt="Sterisul - Centro de Esterilização" 
                className="h-12 w-auto max-w-none object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-4 2xl:gap-8 min-w-0">
              <button onClick={() => scrollToSection('inicio')} className="text-slate-600 hover:text-blue-600 transition-colors">
                Início
              </button>
              <button onClick={() => scrollToSection('servicos')} className="text-slate-600 hover:text-blue-600 transition-colors">
                Serviços
              </button>
              <button onClick={() => scrollToSection('diferenciais')} className="text-slate-600 hover:text-blue-600 transition-colors">
                Diferenciais
              </button>
              <button onClick={() => scrollToSection('contato')} className="text-slate-600 hover:text-blue-600 transition-colors">
                Contato
              </button>
              <Button 
                onClick={() => navigate('/consulta-cliente')}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                Consultar Material
              </Button>
              <Button 
                onClick={() => navigate('/vigilancia')}
                variant="outline"
                className="border-amber-600 text-amber-600 hover:bg-amber-50"
                data-testid="landing-vigilancia"
              >
                Vigilância Sanitária
              </Button>
              <Button 
                onClick={() => navigate('/portal')}
                variant="outline"
                className="border-teal-600 text-teal-600 hover:bg-teal-50"
                data-testid="landing-area-cliente"
              >
                Área do Cliente
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Acessar Sistema
              </Button>
            </nav>

            {/* Mobile menu button */}
            <button 
              className="xl:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="xl:hidden bg-white border-t shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('inicio')} className="block w-full text-left py-2 text-slate-600">
                Início
              </button>
              <button onClick={() => scrollToSection('servicos')} className="block w-full text-left py-2 text-slate-600">
                Serviços
              </button>
              <button onClick={() => scrollToSection('diferenciais')} className="block w-full text-left py-2 text-slate-600">
                Diferenciais
              </button>
              <button onClick={() => scrollToSection('contato')} className="block w-full text-left py-2 text-slate-600">
                Contato
              </button>
              <Button 
                onClick={() => navigate('/consulta-cliente')}
                variant="outline"
                className="w-full border-blue-600 text-blue-600"
              >
                Consultar Material
              </Button>
              <Button 
                onClick={() => navigate('/vigilancia')}
                variant="outline"
                className="w-full border-amber-600 text-amber-600"
              >
                Vigilância Sanitária
              </Button>
              <Button 
                onClick={() => navigate('/portal')}
                variant="outline"
                className="w-full border-teal-600 text-teal-600"
              >
                Área do Cliente
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Acessar Sistema
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="inicio" className="pt-16 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm mb-6">
                <Award className="w-4 h-4" />
                Certificado ANVISA - RDC 15
              </div>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-6">
                Esterilização de
                <span className="text-blue-200"> Alta Qualidade</span> para sua Clínica
              </h1>
              <p className="text-lg lg:text-xl text-blue-100 mb-8 leading-relaxed">
                Centro de Material e Esterilização (CME) especializado em processamento de instrumentais médicos, odontológicos e estéticos com total rastreabilidade e conformidade.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => scrollToSection('contato')}
                  className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg"
                >
                  Solicitar Orçamento
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={() => navigate('/consulta-cliente')}
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg font-semibold"
                >
                  Consultar Material
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-72 h-72 bg-blue-500/30 rounded-full blur-3xl"></div>
                <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold">10+</p>
                      <p className="text-sm text-blue-200">Anos de Experiência</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold">500+</p>
                      <p className="text-sm text-blue-200">Clientes Atendidos</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold">99%</p>
                      <p className="text-sm text-blue-200">Taxa de Aprovação</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold">24h</p>
                      <p className="text-sm text-blue-200">Entrega Rápida</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="relative h-16">
          <svg className="absolute bottom-0 w-full h-16" viewBox="0 0 1440 74" fill="none">
            <path d="M0 74V24.5C240 -5.5 480 -5.5 720 24.5C960 54.5 1200 54.5 1440 24.5V74H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicos" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Nossos Serviços
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Oferecemos soluções completas em esterilização para garantir a segurança dos seus pacientes e profissionais.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                <Microscope className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Esterilização a Vapor</h3>
              <p className="text-slate-600 leading-relaxed">
                Processo de autoclavagem com controle rigoroso de temperatura, pressão e tempo, garantindo a eliminação de todos os microrganismos.
              </p>
            </div>

            {/* Service 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-green-600 transition-colors">
                <FileCheck className="w-7 h-7 text-green-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Rastreabilidade Total</h3>
              <p className="text-slate-600 leading-relaxed">
                Sistema informatizado com QR Code que permite rastrear todo o histórico do material, desde a recepção até a entrega.
              </p>
            </div>

            {/* Service 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-600 transition-colors">
                <Truck className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Coleta e Entrega</h3>
              <p className="text-slate-600 leading-relaxed">
                Serviço de logística com veículos próprios, garantindo a integridade dos materiais durante o transporte.
              </p>
            </div>

            {/* Service 4 */}
            <div className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-600 transition-colors">
                <QrCode className="w-7 h-7 text-purple-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Consulta Online</h3>
              <p className="text-slate-600 leading-relaxed">
                Acompanhe o status dos seus materiais em tempo real pelo celular, escaneando o QR Code das etiquetas.
              </p>
            </div>

            {/* Service 5 */}
            <div className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors">
                <Shield className="w-7 h-7 text-red-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Controle de Qualidade</h3>
              <p className="text-slate-600 leading-relaxed">
                Testes biológicos, químicos e físicos em cada ciclo, com análise por inteligência artificial para maior precisão.
              </p>
            </div>

            {/* Service 6 */}
            <div className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors">
                <MessageCircle className="w-7 h-7 text-teal-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Notificações WhatsApp</h3>
              <p className="text-slate-600 leading-relaxed">
                Receba alertas automáticos sobre o status dos seus materiais, agendamentos e materiais prontos para retirada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Differentials Section */}
      <section id="diferenciais" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Por que escolher a <span className="text-blue-600">Sterisul</span>?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Somos referência em esterilização na região, com equipamentos de última geração e equipe altamente qualificada.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Conformidade ANVISA</h4>
                    <p className="text-slate-600">Atendemos todas as exigências da RDC 15 e normas sanitárias vigentes.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Agilidade na Entrega</h4>
                    <p className="text-slate-600">Ciclos processados em até 24 horas, com opção de urgência.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Equipe Especializada</h4>
                    <p className="text-slate-600">Profissionais treinados e certificados em boas práticas de esterilização.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Award className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Tecnologia de Ponta</h4>
                    <p className="text-slate-600">Sistema informatizado com IA para análise de testes e rastreabilidade.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">Consulte seus Materiais</h3>
                  <p className="text-slate-600 mt-2">Acesse o status em tempo real</p>
                </div>
                
                <div className="space-y-4">
                  <Button 
                    onClick={() => navigate('/consulta-cliente')}
                    className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                  >
                    <QrCode className="w-5 h-5 mr-2" />
                    Consultar por CPF/Telefone
                  </Button>
                  
                  <Button 
                    onClick={() => navigate('/portal')}
                    variant="outline"
                    className="w-full border-blue-600 text-blue-600 hover:bg-blue-50 py-6 text-lg"
                  >
                    Acessar Área do Cliente
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Entre em Contato
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Estamos prontos para atender sua clínica. Solicite um orçamento ou tire suas dúvidas.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center p-6 bg-slate-50 rounded-2xl">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-7 h-7 text-blue-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Telefone</h4>
              <p className="text-slate-600">(46) 92002-3072</p>
              <p className="text-slate-600">(46) 99905-6087 - Orçamento</p>
            </div>

            <div className="text-center p-6 bg-slate-50 rounded-2xl">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-green-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Email</h4>
              <p className="text-slate-600">esteribelpr@gmail.com</p>
            </div>

            <div className="text-center p-6 bg-slate-50 rounded-2xl">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-amber-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Endereço</h4>
              <p className="text-slate-600">Rua Anália da Rocha Dorigoni, Nº 141</p>
              <p className="text-slate-600">Industrial - Francisco Beltrão/PR</p>
              <p className="text-slate-500 text-sm">CEP: 85601-654</p>
            </div>
          </div>

          <div className="text-center">
            <a 
              href="https://wa.me/5546999056087?text=Olá! Gostaria de solicitar um orçamento para esterilização."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 text-white rounded-xl text-lg font-semibold transition-colors"
            >
              <MessageCircle className="w-6 h-6" />
              Fale conosco pelo WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/sterisul-logo.png" 
                  alt="Sterisul" 
                  className="h-10 w-auto object-contain bg-white rounded p-1"
                />
              </div>
              <p className="text-slate-400 mb-4">
                Centro de Material e Esterilização (CME) especializado em processamento de instrumentais médicos, odontológicos e estéticos.
              </p>
              <p className="text-sm text-slate-500">
                © {new Date().getFullYear()} Sterisul. Todos os direitos reservados.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Links Rápidos</h4>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => scrollToSection('inicio')} className="hover:text-white transition-colors">Início</button></li>
                <li><button onClick={() => scrollToSection('servicos')} className="hover:text-white transition-colors">Serviços</button></li>
                <li><button onClick={() => scrollToSection('diferenciais')} className="hover:text-white transition-colors">Diferenciais</button></li>
                <li><button onClick={() => scrollToSection('contato')} className="hover:text-white transition-colors">Contato</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Acesso</h4>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Acessar Sistema</button></li>
                <li><button onClick={() => navigate('/consulta-cliente')} className="hover:text-white transition-colors">Consultar Material</button></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
